// Client layer (TS-2.5.03). A real <table> (not the roadmap board's ARIA-grid-of-divs - every
// cell here is plain text/a link, so a native table expresses it without loss, unlike a board cell
// that can hold several chips) with a <caption>, sortable <th> headers carrying aria-sort, and one
// project per row. Row activation is an ordinary <Link> on the project name rather than a
// click-anywhere row, so keyboard/screen-reader users get the row's one real affordance for free
// instead of a second, hand-rolled one.
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { resolveColor } from "../roadmap/lib/colorTokens";
import { readableTextColor } from "../roadmap/lib/contrast";
import { sortProjects } from "./lib/projectSort";
import type { ProjectSortColumn, SortDirection } from "./lib/projectSort";
import type { ProjectSummary } from "../../api/generated/contracts";
import { strings } from "../../strings";

export interface ProjectsTableProps {
  readonly rows: readonly ProjectSummary[];
  readonly sortColumn: ProjectSortColumn;
  readonly sortDirection: SortDirection;
  readonly onSort: (column: ProjectSortColumn) => void;
}

const COLUMNS: ReadonlyArray<{ column: ProjectSortColumn; label: string }> = [
  { column: "name", label: strings.projects.columns.name },
  { column: "team", label: strings.projects.columns.team },
  { column: "status", label: strings.projects.columns.status },
  { column: "percentComplete", label: strings.projects.columns.percentComplete },
  { column: "updatedAt", label: strings.projects.columns.updated },
];

function formatPercent(value: number | null): string {
  return value === null ? "—" : `${Math.round(value)}%`;
}

function formatUpdated(row: ProjectSummary): string {
  if (!row.updatedAtUtc) return "—";
  const when = new Date(row.updatedAtUtc).toLocaleDateString();
  return row.updatedBy ? `${when} · ${row.updatedBy}` : when;
}

export function ProjectsTable({ rows, sortColumn, sortDirection, onSort }: ProjectsTableProps) {
  const sorted = sortProjects(rows, sortColumn, sortDirection);

  return (
    <table className="eg-projects-table">
      <caption>{strings.projects.tableCaption}</caption>
      <thead>
        <tr>
          {COLUMNS.map(({ column, label }) => {
            const isActive = column === sortColumn;
            return (
              <th key={column} scope="col" aria-sort={isActive ? sortDirection : "none"}>
                <button type="button" onClick={() => onSort(column)}>
                  {label}
                  {isActive && <span aria-hidden="true">{sortDirection === "ascending" ? " ▲" : " ▼"}</span>}
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => (
          <tr key={row.projectKey}>
            <th scope="row">
              <Link to={`/projects/${row.projectKey}`}>{row.name}</Link>
              {row.businessProjectId !== null && (
                <span className="eg-projects-table__business-id"> (#{row.businessProjectId})</span>
              )}
            </th>
            <td>{row.rawTeam ?? "—"}</td>
            <td>
              <span
                className="eg-projects-table__status-pill"
                // Per-value computed text colour, same as the roadmap chips (D-247): the
                // background is the admin-controlled vocabulary hex. projects.css reads both.
                style={
                  {
                    "--eg-chip-color": resolveColor(row.status.colorHex),
                    "--eg-chip-ink": readableTextColor(row.status.colorHex),
                  } as CSSProperties
                }
              >
                {row.status.canonical}
              </span>
            </td>
            <td>{formatPercent(row.percentComplete)}</td>
            <td>{formatUpdated(row)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
